import { usePageTitle } from "@/lib/usePageTitle";
import FadeIn from "@/components/site/FadeIn";
import { PageHero } from "@/components/site/ui";
import { COMPANY, HOSTING, MAIL } from "@/data/content";
import { useLocale } from "@/i18n/LocaleContext";
import { DEFAULT_LOCALE } from "@/i18n/locales";
import RichText from "@/i18n/RichText";

const Block = ({ title, children }) => (
  <div className="mt-8">
    <h2 className="text-2xl font-600 text-brand-dark">{title}</h2>
    <div className="mt-3 text-lg text-muted-foreground leading-relaxed space-y-3">
      {children}
    </div>
  </div>
);

const Paragraphs = ({ items, vars }) =>
  items.map((text) => (
    <p key={text.slice(0, 32)}>
      <RichText text={text} vars={vars} />
    </p>
  ));

export default function Datenschutz() {
  usePageTitle("privacy");
  const { t, code } = useLocale();
  const p = t.privacy;
  const vars = {
    email: COMPANY.email,
    retention: p.retention,
    operator: `${HOSTING.operator.name}, ${HOSTING.operator.address}`,
    provider: `${HOSTING.provider.name}, ${HOSTING.provider.address}`,
    forwarder: `${MAIL.forwarder.name}, ${MAIL.forwarder.address}`,
    mailbox: `${MAIL.mailbox.name}, ${MAIL.mailbox.address}`,
  };
  const hostingLegal = `${p.hosting.ownLegal}${
    HOSTING.provider.url
      ? ` [${HOSTING.provider.url.replace(/^https?:\/\//, "")}](${HOSTING.provider.url})`
      : ""
  }`;

  return (
    <div data-testid="datenschutz-page">
      <PageHero title={p.title} subtitle={p.subtitle} />
      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-20">
          <FadeIn>
            {code !== DEFAULT_LOCALE && (
              <p className="rounded-xl bg-brand-light border border-brand/20 p-4 text-base text-ink">
                {t.common.legalNote}
              </p>
            )}

            <Block title={p.controller.title}>
              <p>{p.controller.lead}</p>
              <p className="text-ink">
                {COMPANY.name}
                <br />
                {p.controller.representedBy} {COMPANY.managingDirector}
                <br />
                <span className="ltr">{COMPANY.address.street}, {COMPANY.address.zip} {COMPANY.address.city}</span>
                <br />
                {p.controller.phone}: <span className="ltr">{COMPANY.phone}</span> · {p.controller.fax}: <span className="ltr">{COMPANY.fax}</span> · {p.controller.email}: {COMPANY.email}
              </p>
            </Block>

            {COMPANY.dpo && (
              <Block title={p.dpo.title}>
                <p>{p.dpo.lead}</p>
                <p className="text-ink">
                  {COMPANY.dpo.name}
                  <br />
                  {COMPANY.address.street}, {COMPANY.address.zip} {COMPANY.address.city}
                  <br />
                  {p.controller.email}: {COMPANY.dpo.email}
                  {COMPANY.dpo.phone ? ` · ${p.controller.phone}: ${COMPANY.dpo.phone}` : ""}
                </p>
              </Block>
            )}

            <Block title={p.general.title}>
              <Paragraphs items={p.general.p} vars={vars} />
            </Block>

            <Block title={p.hosting.title}>
              <p><RichText text={p.hosting.ownIntro} vars={vars} /></p>
              <p><RichText text={p.hosting.logs} vars={vars} /></p>
              <p><RichText text={hostingLegal} vars={vars} /></p>
            </Block>

            <Block title={p.form.title}>
              <Paragraphs items={p.form.p} vars={vars} />
            </Block>

            <Block title={p.emailPhone.title}>
              <Paragraphs items={p.emailPhone.p} vars={vars} />
            </Block>

            <Block title={p.applications.title}>
              <Paragraphs items={p.applications.p} vars={vars} />
            </Block>

            <Block title={p.maps.title}>
              <Paragraphs items={p.maps.p} vars={vars} />
            </Block>

            <Block title={p.rights.title}>
              <p>{p.rights.lead}</p>
              <ul className="list-disc ps-6 space-y-1">
                {p.rights.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p><RichText text={p.rights.how} vars={vars} /></p>
            </Block>

            <Block title={p.authority.title}>
              <p>{p.authority.lead}</p>
              <p className="text-ink">
                {p.authority.name}
                <br />
                <span className="ltr">{p.authority.address}</span>
                <br />
                <a href={p.authority.url} target="_blank" rel="noopener noreferrer" className="text-brand underline underline-offset-4">
                  {p.authority.urlLabel}
                </a>
              </p>
            </Block>

            <Block title={p.security.title}>
              <Paragraphs items={p.security.p} vars={vars} />
            </Block>

            <Block title={p.changes.title}>
              <Paragraphs items={p.changes.p} vars={vars} />
              <p className="text-base">{p.lastUpdated}</p>
            </Block>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
